/**
 * CompanyAssessmentRoute — candidate-facing MCQ / Coding tests for company
 * jobs (event_type MCQ / CODING), reached through the same public
 * /interview/:jobId link as AI-interview jobs. JobInterviewRoute branches
 * into CompanyMcqTest / CompanyCodingTest once it knows the job's event type.
 *
 * The coding editor reuses <CodingTest> from InstCodingRoute.jsx (Monaco
 * editor, Judge0 run, autosave/retry/offline-queue submit logic) via its
 * runEndpoint/statusEndpoint overrides rather than duplicating that logic.
 */

import React, { useEffect, useRef, useState } from "react";
import { CodingTest } from "../InstCodingRoute";
import { AssessmentTest } from "../AssessmentScreen";
import useCampaignProctoring from "../hooks/useCampaignProctoring";
import { isScreenShareSupported } from "../utils/proctoringSupport";
import {
  getJobMcq, submitJobMcq,
  getJobCoding, submitJobCoding,
  getJobAttemptStatus, saveJobProctoring
} from "../services/jobService";
import "../InstitutionalDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

/* ── Self-view camera overlay (proctoring) ─────────────────────────────── */

function SelfViewCamera({ onStream, onStatus }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("requesting");

  useEffect(() => {
    let stream = null;
    navigator.mediaDevices
      .getUserMedia({ video: { width: 160, height: 120, facingMode: "user" }, audio: false })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        setStatus("active");
        onStream?.(s);
        onStatus?.("active");
      })
      .catch(() => { setStatus("denied"); onStatus?.("denied"); });
    return () => { stream?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line

  if (status === "denied") {
    return (
      <div className="inst-selfview inst-selfview--denied">
        <span>📷</span>
        <p>Camera denied</p>
      </div>
    );
  }
  return (
    <div className="inst-selfview">
      <video ref={videoRef} className="inst-selfview__video" muted playsInline autoPlay />
      <div className="inst-selfview__label">
        <span className="inst-selfview__dot" />
        Proctored
      </div>
    </div>
  );
}

function CameraDeniedCard() {
  return (
    <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
      <div className="inst-mcq-error-icon">📷</div>
      <h2>Camera Access Required</h2>
      <p>This assessment requires camera access for proctoring. Please allow camera access in your browser settings, then reload the page.</p>
      <button className="inst-btn-primary" style={{ marginTop: "1.25rem" }} onClick={() => window.location.reload()}>
        Reload &amp; Try Again
      </button>
    </div>
  );
}

/* ── Screen-share + fullscreen gate (proctoring) ───────────────────────────
   Shown after camera succeeds, before the test renders. Screen share MUST
   come first — Chrome's native "choose what to share" picker itself drops
   the page out of fullscreen, so requesting fullscreen first only gets
   silently undone the moment the share picker opens. Two sequential steps,
   each requiring its own user gesture (both prompts need one). */

function ProctoringGateCard({
  screenShareStatus, onShareScreen,
  fullscreenStatus, onEnterFullscreen, fullscreenAttempts, onSkipFullscreen,
}) {
  // "unsupported" = the device's browser has no Screen Capture API at all
  // (every iOS browser, some older desktop browsers) — that's not something a
  // "Try Again" click can ever fix, so it's treated as satisfied rather than
  // shown as a blocking requirement. The recruiter still sees this on the
  // candidate's record (see screenShareUnsupported below).
  const screenShareDone = screenShareStatus === "active" || screenShareStatus === "unsupported";

  return (
    <div className="proctor-gate-center">
    <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
      <div className="inst-mcq-error-icon">🖥️</div>
      <h2>Proctoring Setup</h2>
      <p>This assessment requires screen sharing and fullscreen mode so it can be proctored.</p>

      {!screenShareDone ? (
        <>
          <button className="inst-btn-primary" style={{ marginTop: "1.25rem" }} onClick={onShareScreen}>
            {screenShareStatus ? "Try Again" : "Share Your Screen"}
          </button>
          {screenShareStatus === "denied" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Screen sharing is required to continue. Please select a screen and click Share.
            </p>
          )}
          {screenShareStatus === "wrong_surface" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Please share your <strong>Entire Screen</strong>, not a specific window or tab.
            </p>
          )}
        </>
      ) : (
        <>
          {screenShareStatus === "unsupported" ? (
            <p style={{ color: "#f5a524", fontSize: "0.9rem" }}>
              Screen sharing isn't available on this device — continuing with camera and fullscreen proctoring only.
            </p>
          ) : (
            <p style={{ color: "#36c8a0", fontSize: "0.9rem" }}>✓ Screen sharing active</p>
          )}
          <button className="inst-btn-primary" style={{ marginTop: "0.5rem" }} onClick={onEnterFullscreen}>
            {fullscreenAttempts > 0 ? "Try Again" : "Enter Fullscreen"}
          </button>
          {fullscreenStatus === "failed" && (
            <>
              <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
                Could not enter fullscreen mode.
              </p>
              {fullscreenAttempts > 0 && (
                <button className="link-button" style={{ marginTop: "0.25rem" }} onClick={onSkipFullscreen}>
                  Continue without fullscreen
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
    </div>
  );
}

function AlreadySubmittedCard() {
  return (
    <div className="inst-already-done">
      <div className="inst-already-done__icon">✓</div>
      <h2>Assessment Already Submitted</h2>
      <p>You have already completed this assessment. Each link can only be attempted once.</p>
    </div>
  );
}

/* ── MCQ test ───────────────────────────────────────────────────────────── */

function adaptCodingQuestions(questions) {
  return questions.map((q, i) => ({
    problem_id: q.question_id,
    title: `Problem ${i + 1}`,
    difficulty: "MEDIUM",
    description: q.question,
    starter_code: {},
    visible_testcases: q.test_cases || [],
    // Was previously dropped in this mapping entirely, so CodingTest always
    // defaulted every problem to Python regardless of what language the
    // recruiter actually wrote the reference solution in.
    reference_language: q.reference_language || "python",
  }));
}

export function CompanyMcqTest({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alreadyAttempted, setAlreadyAttempted] = useState(null);
  // "mcq" (AssessmentTest, matches the college dashboard's test-taking layout)
  // → "coding" (only when config.include_coding) → "done"
  const [phase, setPhase] = useState("mcq");
  const [codingProblems, setCodingProblems] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  // Optional recruiter-configured hard deadline for the WHOLE test
  // (config.total_minutes, set at job creation). Computed once and carried
  // across the MCQ -> Coding phase transition — persisted in sessionStorage
  // so a page refresh mid-test doesn't hand back extra time. Jobs without
  // total_minutes set leave this null: MCQ keeps its existing default
  // countdown and the coding round stays unlimited, exactly as before.
  const [deadlineEpoch, setDeadlineEpoch] = useState(() => {
    const saved = sessionStorage.getItem(`vagmee_company_deadline_${jobId}`);
    const parsed = saved ? Number(saved) : null;
    // A deadline left over from an earlier attempt in this SAME browser tab
    // (e.g. the same test link opened more than once without closing the
    // tab) would already be in the past — restoring it as-is fires the
    // deadline-auto-submit effect within moments of mounting, submitting an
    // empty MCQ before the candidate can even answer, then immediately
    // auto-submitting Coding too via the same carried-forward value. Only
    // trust it if it's still actually in the future.
    if (parsed && parsed <= Date.now()) {
      sessionStorage.removeItem(`vagmee_company_deadline_${jobId}`);
      return null;
    }
    return parsed;
  });
  // Epoch ms of the last recruiter-granted retake for this candidate (null if
  // never retaken). Passed through to AssessmentTest/CodingTest so they can
  // discard a leftover localStorage draft from the attempt BEFORE this
  // retake instead of restoring already-answered questions into a test that
  // is supposed to start fresh.
  const [minDraftTimestamp, setMinDraftTimestamp] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraStatus, setCameraStatus] = useState(null);
  const [fullscreenStatus, setFullscreenStatus] = useState(null); // null|"active"|"failed"|"skipped"
  const [fullscreenAttempts, setFullscreenAttempts] = useState(0);
  const [screenStream, setScreenStream] = useState(null);
  const [screenShareStatus, setScreenShareStatus] = useState(null); // null|"active"|"denied"|"wrong_surface"|"unsupported"
  const startRef = useRef(Date.now());
  const lastAnswersRef = useRef(null);

  // Recruiter-configured escape hatch (config.proctoring_disabled, set at job
  // creation) for candidates who may not have a camera, screen-share support,
  // or a browser that allows fullscreen — turns off camera/screen-share/
  // fullscreen requirements AND all signal collection (tab-switch, copy-
  // paste, gaze, etc.) for this job entirely, not just the visible gate.
  const proctoringEnabled = !(data?.config?.proctoring_disabled);

  // iOS has no Screen Capture API at all (permanent, not a permission the
  // candidate can grant) — detect that up front and skip the step entirely
  // instead of showing an unwinnable "required" prompt. Still flagged on the
  // candidate's proctoring record so recruiters can see it happened.
  useEffect(() => {
    if (!data || !proctoringEnabled) return;
    if (!isScreenShareSupported()) {
      setScreenShareStatus("unsupported");
      saveJobProctoring(jobId, { screenShareUnsupported: true }).catch(() => {});
    }
  }, [jobId, data, proctoringEnabled]);

  const handleEnterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenStatus("active");
    } catch (_) {
      setFullscreenStatus("failed");
      setFullscreenAttempts((n) => n + 1);
    }
  };

  const handleShareScreen = async () => {
    try {
      // displaySurface: "monitor" hints the browser's picker toward "Entire
      // Screen" (Chrome pre-selects that tab of the picker) — it's a hint,
      // not an enforceable restriction, so the actual selection is still
      // validated below and rejected if the candidate picked a window/tab.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const surface = track?.getSettings?.().displaySurface;
      if (surface && surface !== "monitor") {
        stream.getTracks().forEach((t) => t.stop());
        setScreenShareStatus("wrong_surface");
        return;
      }
      setScreenStream(stream);
      setScreenShareStatus("active");
    } catch (_) {
      setScreenShareStatus("denied");
    }
  };

  useEffect(() => {
    return () => { screenStream?.getTracks().forEach((t) => t.stop()); };
  }, [screenStream]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, status] = await Promise.all([getJobMcq(jobId), getJobAttemptStatus(jobId)]);
        if (cancelled) return;
        setData(meta);
        startRef.current = Date.now();
        if (status.retaken_at) setMinDraftTimestamp(status.retaken_at);

        const totalMinutes = Number(meta.config?.total_minutes) || 0;
        if (totalMinutes > 0) {
          setDeadlineEpoch((prev) => {
            if (prev) return prev; // already running (page refresh mid-test)
            const epoch = Date.now() + totalMinutes * 60 * 1000;
            sessionStorage.setItem(`vagmee_company_deadline_${jobId}`, String(epoch));
            return epoch;
          });
        }

        const combined = !!meta.config?.include_coding;
        if (status.coding_done || (status.mcq_done && !combined)) {
          // Fully done (or an MCQ-only job whose MCQ half is already submitted).
          setAlreadyAttempted(true);
        } else if (status.mcq_done && combined) {
          // MCQ half done but the coding half never completed (e.g. a dropped
          // connection mid-submit) — resume straight into the coding round
          // instead of dead-ending on "already submitted".
          setAlreadyAttempted(false);
          try {
            const codingMeta = await getJobCoding(jobId);
            if (cancelled) return;
            setCodingProblems(adaptCodingQuestions(codingMeta.questions || []));
            setPhase("coding");
          } catch (err) {
            if (!cancelled) setError(err.message || "Unable to load the coding round.");
          }
        } else {
          setAlreadyAttempted(false);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load assessment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const { warning, integrityScore, integrityLevel, signals, isFullscreenActive, isScreenShareLive, flush: flushProctoring } = useCampaignProctoring({
    enabled: !loading && alreadyAttempted === false && phase !== "done" && proctoringEnabled,
    campaignId: jobId,
    mediaStream: cameraStream,
    screenStream,
    endpoint: `${API_BASE}/company/jobs/${jobId}/proctoring`,
  });

  // Live-state-driven proctoring enforcement — ONE combined overlay covering
  // both fullscreen and screen-share, instead of two independent
  // counter-threshold overlays. Two independent overlays could collide: on
  // Windows/Chrome, clicking the browser's native "Stop sharing" control
  // also forces an exit from fullscreen (interacting with browser chrome
  // while fullscreen kicks the page out of it), so a single candidate
  // action could trip both at once — only the top-stacked overlay was ever
  // visible/clickable, silently orphaning the other, and the candidate saw
  // no way back from whichever one was hidden underneath.
  //
  // Deliberately no auto-submit-after-N-violations here (there used to be
  // one). Resolving screen-share also opens a native browser picker, which
  // can itself momentarily kick the candidate back out of fullscreen as a
  // side effect — so a candidate legitimately fixing ONE violation could
  // coincidentally trip the OTHER a moment later, registering as a second
  // "strike" and silently force-submitting them into the next phase even
  // though they never did anything wrong beyond the original, already-being-
  // corrected violation. The overlay itself IS the enforcement — the
  // candidate simply cannot interact with the test while non-compliant —
  // and every violation still increments fullscreenExitCount /
  // screenShareStoppedCount into the integrity score for the recruiter to
  // review and judge, rather than the system unilaterally ending a
  // legitimate candidate's attempt over a timing coincidence.
  const fullscreenBroken = fullscreenStatus === "active" && !isFullscreenActive;
  const screenShareBroken = screenShareStatus === "active" && !isScreenShareLive;
  const midTestBlocked = proctoringEnabled && (fullscreenBroken || screenShareBroken);

  const handleReturnToFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); } catch (_) {}
  };

  const saveProctoringNow = async () => {
    if (!proctoringEnabled) return;
    // Uses the hook's own flush() (a full signal snapshot), not a hand-built
    // partial payload -- a partial {integrityScore, integrityLevel}-only PATCH
    // here previously raced with the hook's periodic full-signal sync and, on
    // the backend, wiped every other already-recorded count back to 0.
    try { await flushProctoring(); } catch (_) {}
  };

  const questionById = {};
  (data?.questions || []).forEach((q) => { questionById[q.question_id] = q; });

  // Map to the same shape AssessmentTest already renders for institutional /
  // practice MCQs — minus `correct`, which is never sent to the browser for a
  // company's reusable question bank (see security note on the backend route).
  const assessmentQuestions = (data?.questions || []).map((q) => ({
    id: q.question_id,
    section: "general",
    topic: "",
    difficulty: "medium",
    text: q.question,
    options: { A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d },
  }));

  const handleMcqSubmit = async (finalAnswers) => {
    lastAnswersRef.current = finalAnswers;
    setSubmitError(null);
    try {
      const answersArray = Object.entries(finalAnswers).map(([questionId, letter]) => {
        const q = questionById[questionId];
        const optionKey = `option_${String(letter).toLowerCase()}`;
        return { question_id: questionId, selected: q?.[optionKey] || "" };
      });
      const timeUsedMs = Date.now() - startRef.current;
      // The response includes the candidate's own score, but it's never shown
      // to them — only the recruiter dashboard displays results for a hiring
      // assessment (matches how AI-interview scores are handled too).
      await submitJobMcq(jobId, answersArray, timeUsedMs);

      if (data.config?.include_coding) {
        const codingMeta = await getJobCoding(jobId);
        setCodingProblems(adaptCodingQuestions(codingMeta.questions || []));
        setPhase("coding");
      } else {
        await saveProctoringNow();
        setPhase("done");
      }
    } catch (err) {
      setSubmitError(err.message || "Unable to submit assessment. Your answers are preserved — please retry.");
    }
  };

  const handleCodingSubmitAll = async (submissions) => {
    const receipt = await submitJobCoding(jobId, submissions);
    await saveProctoringNow();
    return receipt;
  };

  if (loading || alreadyAttempted === null) {
    return <div className="inst-mcq-loading">Loading assessment…</div>;
  }
  if (error) {
    return (
      <div className="inst-mcq-error-card">
        <div className="inst-mcq-error-icon">⚠️</div>
        <h2>Assessment Unavailable</h2>
        <p>{error}</p>
      </div>
    );
  }
  if (alreadyAttempted) return <AlreadySubmittedCard />;
  if (cameraStatus === "denied") return <CameraDeniedCard />;

  if (!data.questions || data.questions.length === 0) {
    return (
      <div className="inst-mcq-error-card">
        <div className="inst-mcq-error-icon">📭</div>
        <h2>No Questions Available</h2>
        <p>The question bank for this assessment is empty. Please contact the hiring team.</p>
      </div>
    );
  }

  if (submitError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "1rem", padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "2rem" }}>⚠️</div>
        <h2 style={{ margin: 0, fontSize: "1.2rem" }}>Submission Failed</h2>
        <p style={{ margin: 0, color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", maxWidth: 400 }}>{submitError}</p>
        <button
          className="inst-btn-primary"
          onClick={() => lastAnswersRef.current && handleMcqSubmit(lastAnswersRef.current)}
        >
          Retry Submission
        </button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="instc-done">
        <div className="instc-done-icon">✓</div>
        <h2>Assessment Complete</h2>
        <p>Your responses have been submitted for review.</p>
        <p className="instc-done-sub">Your responses have been recorded. You may close this tab.</p>
      </div>
    );
  }

  const proctoringGateComplete = !proctoringEnabled || (
    (screenShareStatus === "active" || screenShareStatus === "unsupported") &&
    (fullscreenStatus === "active" || fullscreenStatus === "skipped")
  );

  // SelfViewCamera (and the warning banner) render in this one stable spot
  // for BOTH the mcq and coding phases, rather than once per phase-specific
  // return — otherwise switching phase unmounts and remounts it, stopping
  // the actual camera tracks (SelfViewCamera's cleanup) and silently killing
  // webcam/gaze-based proctoring for the rest of the test the moment the
  // candidate reaches the coding round.
  return (
    <div style={{ position: "relative" }}>
      {warning && (
        <div className="inst-proctor-banner">
          <span className="inst-proctor-icon">⚠</span>
          {warning}
        </div>
      )}
      {proctoringEnabled && <SelfViewCamera onStream={setCameraStream} onStatus={setCameraStatus} />}
      {phase === "coding" ? (
        !codingProblems.length ? (
          <div className="inst-mcq-loading">Loading coding round…</div>
        ) : (
          <CodingTest
            campaign={{ title: data.title, college_name: data.company_name }}
            problems={codingProblems}
            onSubmitAll={handleCodingSubmitAll}
            campaignId={jobId}
            runEndpoint={`${API_BASE}/api/institutional/coding/run`}
            statusEndpoint={`${API_BASE}/company/jobs/${jobId}/my-status`}
            deadlineEpoch={deadlineEpoch}
            minDraftTimestamp={minDraftTimestamp}
            hideScore
            hideSubmitUntilLast
          />
        )
      ) : proctoringGateComplete ? (
        <AssessmentTest
          questions={assessmentQuestions}
          onSubmit={handleMcqSubmit}
          campaignId={jobId}
          deadlineEpoch={deadlineEpoch}
          minDraftTimestamp={minDraftTimestamp}
          hideSubmitUntilLast
        />
      ) : (
        <ProctoringGateCard
          screenShareStatus={screenShareStatus}
          onShareScreen={handleShareScreen}
          fullscreenStatus={fullscreenStatus}
          fullscreenAttempts={fullscreenAttempts}
          onEnterFullscreen={handleEnterFullscreen}
          onSkipFullscreen={() => setFullscreenStatus("skipped")}
        />
      )}
      {midTestBlocked && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center", background: "rgba(5,3,15,0.85)", backdropFilter: "blur(6px)" }}>
          <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Proctoring Requirement Not Met</h2>
            {fullscreenBroken && <p>You exited fullscreen mode.</p>}
            {screenShareBroken && <p>You stopped sharing your screen.</p>}
            <p style={{ color: "#f87171", fontSize: "0.85rem" }}>
              This has been recorded as a proctoring violation. If this happens again, your test will be submitted automatically.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1.25rem", flexWrap: "wrap" }}>
              {fullscreenBroken && (
                <button className="inst-btn-primary" onClick={handleReturnToFullscreen}>Return to Fullscreen</button>
              )}
              {screenShareBroken && (
                <button className="inst-btn-primary" onClick={handleShareScreen}>Resume Screen Sharing</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Coding test ────────────────────────────────────────────────────────── */

export function CompanyCodingTest({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alreadyAttempted, setAlreadyAttempted] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraStatus, setCameraStatus] = useState(null);
  // Standalone coding-only jobs previously had no screen-share/fullscreen
  // gate or mid-test enforcement at all — only the combined MCQ+Coding flow
  // (CompanyMcqTest) did. This mirrors that component's gate + 2-strike
  // enforcement exactly, so a coding-only assessment is proctored the same
  // way a combined one already is.
  const [fullscreenStatus, setFullscreenStatus] = useState(null); // null|"active"|"failed"|"skipped"
  const [fullscreenAttempts, setFullscreenAttempts] = useState(0);
  const [screenStream, setScreenStream] = useState(null);
  const [screenShareStatus, setScreenShareStatus] = useState(null); // null|"active"|"denied"|"wrong_surface"|"unsupported"
  // Optional recruiter-configured hard deadline (config.total_minutes) —
  // absent unless the recruiter set one at job creation, in which case the
  // coding round stays unlimited exactly as before.
  const [deadlineEpoch, setDeadlineEpoch] = useState(() => {
    const saved = sessionStorage.getItem(`vagmee_company_deadline_${jobId}`);
    const parsed = saved ? Number(saved) : null;
    // See the matching check in CompanyMcqTest for why this exists — an
    // already-expired leftover deadline from an earlier attempt in this same
    // tab would otherwise auto-submit within moments of mounting.
    if (parsed && parsed <= Date.now()) {
      sessionStorage.removeItem(`vagmee_company_deadline_${jobId}`);
      return null;
    }
    return parsed;
  });
  // See the matching field in CompanyMcqTest for why this exists.
  const [minDraftTimestamp, setMinDraftTimestamp] = useState(null);
  // See the matching field in CompanyMcqTest for why this exists.
  const proctoringEnabled = !(data?.config?.proctoring_disabled);

  useEffect(() => {
    if (!data || !proctoringEnabled) return;
    if (!isScreenShareSupported()) {
      setScreenShareStatus("unsupported");
      saveJobProctoring(jobId, { screenShareUnsupported: true }).catch(() => {});
    }
  }, [jobId, data, proctoringEnabled]);

  const handleEnterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenStatus("active");
    } catch (_) {
      setFullscreenStatus("failed");
      setFullscreenAttempts((n) => n + 1);
    }
  };

  const handleShareScreen = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const surface = track?.getSettings?.().displaySurface;
      if (surface && surface !== "monitor") {
        stream.getTracks().forEach((t) => t.stop());
        setScreenShareStatus("wrong_surface");
        return;
      }
      setScreenStream(stream);
      setScreenShareStatus("active");
    } catch (_) {
      setScreenShareStatus("denied");
    }
  };

  useEffect(() => {
    return () => { screenStream?.getTracks().forEach((t) => t.stop()); };
  }, [screenStream]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, status] = await Promise.all([getJobCoding(jobId), getJobAttemptStatus(jobId)]);
        if (cancelled) return;
        setData(meta);
        setAlreadyAttempted(status.attempted);
        if (status.retaken_at) setMinDraftTimestamp(status.retaken_at);

        const totalMinutes = Number(meta.config?.total_minutes) || 0;
        if (totalMinutes > 0) {
          setDeadlineEpoch((prev) => {
            if (prev) return prev;
            const epoch = Date.now() + totalMinutes * 60 * 1000;
            sessionStorage.setItem(`vagmee_company_deadline_${jobId}`, String(epoch));
            return epoch;
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load assessment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const { warning, integrityScore, integrityLevel, signals, isFullscreenActive, isScreenShareLive, flush: flushProctoring } = useCampaignProctoring({
    enabled: !loading && alreadyAttempted === false && !submitted && proctoringEnabled,
    campaignId: jobId,
    mediaStream: cameraStream,
    screenStream,
    endpoint: `${API_BASE}/company/jobs/${jobId}/proctoring`,
  });

  // Live-state-driven proctoring enforcement — see the matching comment in
  // CompanyMcqTest for why there's no auto-submit-after-N-violations here;
  // the overlay itself is the enforcement, violations just feed the
  // integrity score for the recruiter to review.
  const fullscreenBroken = fullscreenStatus === "active" && !isFullscreenActive;
  const screenShareBroken = screenShareStatus === "active" && !isScreenShareLive;
  const midTestBlocked = proctoringEnabled && (fullscreenBroken || screenShareBroken);

  const handleReturnToFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); } catch (_) {}
  };

  const handleSubmitAll = async (submissions) => {
    const res = await submitJobCoding(jobId, submissions);
    if (proctoringEnabled) {
      // Full signal snapshot via the hook's own flush() -- see the matching
      // comment in CompanyMcqTest's saveProctoringNow for why not a hand-built
      // partial payload.
      try { await flushProctoring(); } catch (_) {}
    }
    setSubmitted(true);
    return res;
  };

  if (loading || alreadyAttempted === null) {
    return <div className="inst-mcq-loading">Loading coding test…</div>;
  }
  if (error) {
    return (
      <div className="inst-mcq-error-card">
        <div className="inst-mcq-error-icon">⚠️</div>
        <h2>Test Unavailable</h2>
        <p>{error}</p>
      </div>
    );
  }
  if (alreadyAttempted) return <AlreadySubmittedCard />;
  if (cameraStatus === "denied") return <CameraDeniedCard />;

  if (!data.questions || data.questions.length === 0) {
    return (
      <div className="inst-mcq-error-card">
        <div className="inst-mcq-error-icon">📭</div>
        <h2>No Problems Available</h2>
        <p>The problem pool for this test is empty. Please contact the hiring team.</p>
      </div>
    );
  }

  const problems = adaptCodingQuestions(data.questions);
  const proctoringGateComplete = !proctoringEnabled || (
    (screenShareStatus === "active" || screenShareStatus === "unsupported") &&
    (fullscreenStatus === "active" || fullscreenStatus === "skipped")
  );

  return (
    <div style={{ position: "relative" }}>
      {warning && (
        <div className="inst-proctor-banner">
          <span className="inst-proctor-icon">⚠</span>
          {warning}
        </div>
      )}
      {!submitted && proctoringEnabled && <SelfViewCamera onStream={setCameraStream} onStatus={setCameraStatus} />}
      {proctoringGateComplete ? (
        <CodingTest
          campaign={{ title: data.title, college_name: data.company_name }}
          problems={problems}
          onSubmitAll={handleSubmitAll}
          campaignId={jobId}
          runEndpoint={`${API_BASE}/api/institutional/coding/run`}
          statusEndpoint={`${API_BASE}/company/jobs/${jobId}/my-status`}
          deadlineEpoch={deadlineEpoch}
          minDraftTimestamp={minDraftTimestamp}
          hideScore
        />
      ) : (
        <ProctoringGateCard
          screenShareStatus={screenShareStatus}
          onShareScreen={handleShareScreen}
          fullscreenStatus={fullscreenStatus}
          fullscreenAttempts={fullscreenAttempts}
          onEnterFullscreen={handleEnterFullscreen}
          onSkipFullscreen={() => setFullscreenStatus("skipped")}
        />
      )}
      {midTestBlocked && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center", background: "rgba(5,3,15,0.85)", backdropFilter: "blur(6px)" }}>
          <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Proctoring Requirement Not Met</h2>
            {fullscreenBroken && <p>You exited fullscreen mode.</p>}
            {screenShareBroken && <p>You stopped sharing your screen.</p>}
            <p style={{ color: "#f87171", fontSize: "0.85rem" }}>
              This has been recorded as a proctoring violation. If this happens again, your test will be submitted automatically.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1.25rem", flexWrap: "wrap" }}>
              {fullscreenBroken && (
                <button className="inst-btn-primary" onClick={handleReturnToFullscreen}>Return to Fullscreen</button>
              )}
              {screenShareBroken && (
                <button className="inst-btn-primary" onClick={handleShareScreen}>Resume Screen Sharing</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
